import * as React from "react";
import Linkify from "react-linkify";
import { VideoProjectServices } from "@/services";
import { ClientNotes } from "@/interfaces";
import { Dialog, Button } from "@/components/ui";
import { StickyNote, Copy, Check } from "lucide-react";
import copy from "copy-to-clipboard";
import TextareaAutosize from "react-textarea-autosize";
import { cn } from "@/lib/utils";

import { useEffect, useState } from "react";

interface Props {
  playerId?: number;
  coachId?: string;
  playerNotes?: ClientNotes;
  coachNotes?: ClientNotes;
  onUpdate: (pNotes?: ClientNotes, cNotes?: ClientNotes) => void;
}

const ClientNotesFC: React.FC<Props> = ({ playerId, coachId, playerNotes, coachNotes, onUpdate }) => {
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [playerNotesText, setPlayerNotesText] = useState<string>("");
  const [coachNotesText, setCoachNotesText] = useState<string>("");
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const handleCopyUrl = (url: string) => {
    copy(url);
    setCopiedUrl(url);
    setTimeout(() => {
      setCopiedUrl(null);
    }, 2000);
  };

  useEffect(() => {
    setPlayerNotesText(playerNotes?.notes || "");
  }, [playerNotes?.notes]);

  useEffect(() => {
    setCoachNotesText(coachNotes?.notes || "");
  }, [coachNotes?.notes]);

  const updateClientNotes = async () => {
    const savedPlayerNotes =
      playerId && (playerNotesText || playerNotes)
        ? await VideoProjectServices.addClientNotes(playerId, playerNotesText)
        : undefined;
    const savedCoachNotes =
      coachId && (coachNotesText || coachNotes)
        ? await VideoProjectServices.addCoachClientNotes(coachId, coachNotesText)
        : undefined;

    onUpdate(savedPlayerNotes, savedCoachNotes);
    setShowNotesModal(false);
  };

  const hasNotes = !!coachNotes?.notes || !!playerNotes?.notes;

  const linkDecorator = (decoratedHref: string, decoratedText: string, key: string | number) => {
    const truncatedText = decoratedText.length > 30 ? `${decoratedText.substring(0, 27)}...` : decoratedText;
    return (
      <span key={key} className="inline-flex items-center gap-1 select-none">
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
          href={decoratedHref}
          title={decoratedText}
        >
          {truncatedText}
        </a>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            handleCopyUrl(decoratedHref);
          }}
          className="p-0.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-all cursor-pointer inline-flex items-center justify-center border-none outline-none"
          title="Copy link to clipboard"
        >
          {copiedUrl === decoratedHref ? (
            <Check className="size-3 text-emerald-600 animate-bounce" />
          ) : (
            <Copy className="size-3" />
          )}
        </button>
      </span>
    );
  };

  return (
    <>
      <div className="flex flex-col w-full gap-3 mt-4">
        <div className="flex flex-row justify-between items-center w-full select-none">
          <label className="sectionHeader font-semibold text-slate-800 text-sm flex items-center gap-1.5">
            <StickyNote className="size-4.5 text-emerald-600" />
            Client Notes
          </label>
          <span
            className="text-primary hover:underline font-semibold text-xs cursor-pointer"
            data-elm-id="addClientNotesBtn"
            onClick={() => setShowNotesModal(true)}
          >
            {!hasNotes ? "Add" : "Edit"}
          </span>
        </div>

        {hasNotes ? (
          <div className="videoProjectContainer flex flex-col gap-4 bg-emerald-50/10 p-5 rounded-xl border border-emerald-100 border-l-4 border-l-emerald-500 shadow-xs text-left">
            {!!coachNotes?.notes && (
              <div className="flex flex-col gap-1.5 text-sm">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider select-none">
                  Coach Notes (all players)
                </div>
                <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                  <Linkify componentDecorator={linkDecorator}>{coachNotes.notes}</Linkify>
                </div>
              </div>
            )}

            {!!coachNotes?.notes && !!playerNotes?.notes && <div className="border-t border-slate-200/60 my-1" />}

            {!!playerNotes?.notes && (
              <div className="flex flex-col gap-1.5 text-sm">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider select-none">
                  Player Notes
                </div>
                <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                  <Linkify componentDecorator={linkDecorator}>{playerNotes.notes}</Linkify>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="videoProjectContainer flex flex-col items-center justify-center p-5 rounded-xl border border-slate-200 bg-slate-50/50 w-full text-center py-6 select-none gap-1.5">
            <span className="text-slate-400 text-xs font-medium">No client notes added yet.</span>
            <button
              type="button"
              onClick={() => setShowNotesModal(true)}
              className="text-primary hover:underline text-xs font-semibold cursor-pointer"
            >
              Add notes
            </button>
          </div>
        )}
      </div>

      {showNotesModal && (
        <Dialog open={showNotesModal} onOpenChange={setShowNotesModal} size="md" showCloseButton={true}>
          <Dialog.Header>
            <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-base font-bold select-none">
              <StickyNote className="size-5 text-emerald-600 shrink-0" />
              <span>Edit Client Notes</span>
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Content className="p-5 flex flex-col gap-4 text-left">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-700">
                Coach Notes (for all players in this account)
              </label>
              <TextareaAutosize
                data-elm-id="notesInputCoach"
                placeholder="Add coach notes..."
                minRows={3}
                className={cn(
                  "flex w-full rounded-lg border border-input bg-white px-3 py-2 text-base placeholder:text-muted-foreground outline-none transition-colors focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none"
                )}
                value={coachNotesText}
                onChange={e => setCoachNotesText(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-700">Player Notes</label>
              <TextareaAutosize
                data-elm-id="notesInputPlayer"
                placeholder="Add player notes..."
                minRows={3}
                className={cn(
                  "flex w-full rounded-lg border border-input bg-white px-3 py-2 text-base placeholder:text-muted-foreground outline-none transition-colors focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none"
                )}
                value={playerNotesText}
                onChange={e => setPlayerNotesText(e.target.value)}
              />
            </div>
          </Dialog.Content>

          <Dialog.Footer className="flex flex-row justify-end gap-2.5 p-5 border-t border-slate-100 select-none">
            <Button variant="outline" size="sm" onClick={() => setShowNotesModal(false)}>
              Cancel
            </Button>
            <Button variant="default" size="sm" onClick={updateClientNotes}>
              Save
            </Button>
          </Dialog.Footer>
        </Dialog>
      )}
    </>
  );
};

export const ClientNotesComponent = ClientNotesFC;
